/**
 * @class
 * @todo Document members and methods
 */
import Base from '@netx/core/lib/netx-collection';
import Model from '../models/category';

import customConfig from '../config';
import {getPreference} from '@netx/core-user-next/utils/getPreference';

export default class CategoryTreeNodes extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/** @type {array} */
	get xhrExcludeProgress() {
		return ['getCategoryAutoList'];
	}

	/** @type {object} */
	get xhrLoadingMessages() {
		return {
			getCategories: 'Loading categories',
			getCategoryObjects: 'Loading categories',
			setCategoryOrder: 'Updating category order',
		};
	}

	/** @type {object} */
	get xhrLoadedMessages() {
		return {
			getCategories: 'Loading categories',
			getCategoryObjects: 'Loaded categories',
			setCategoryOrder: 'Updated category order',
		};
	}

	/**
	 * Methods for this model
	 * @type {object}
	 */
	get methods() {
		return {
			read: [
				'getCategories',
				'searchString',
				function () {
					return customConfig.categoryid;
				},
			],
			_fetchChildren: ['getCategories', 'searchString', 'targetCategory'],
			_fetchCategory: ['getCategoryData', 'targetCategory'],
			_searchCategoryName: ['getCategoryAutoList', 'searchString', 1],
			_getCategoryObjects: ['getCategoryObjects', 'neededCats'],
			_setCategoryOrder() {
				// Order by 'order' and then pluck the category ids
				// Use compact as we may have had a `dummy` category in there ("empty")
				// TODO: look into the 'threaded' piece (we are currently passing false)
				return [
					'setCategoryOrder',
					_.compact(_.pluck(_.sortBy(this.order, 'order'), 'categoryid')),
					this.orderType,
					false,
				];
			},
		};
	}

	/**
	 * @property {object}   parsers                - Collection parsers
	 * @property {function} parsers._read          - Parse results of fetch
	 * @property {function} parsers._fetchChildren - Parse the result of fetch children
	 * @property {function} parsers._fetchCategory - Parse the result of fetch category
	 */
	get parsers() {
		return {
			// read parser is the parse() method below
			_fetchChildren(data) {
				data = data || [];
				//return this.parseCats(data);	// Backbone.RPC ignores return value of parsers, feh.
				this.parseCats(data);
				return;
			},
			_fetchCategory(data) {
				this.parseCats([data]);
				return;
			},
		};
	}

	/** @override */
	parse(data) {
		// So we know to use a reset after parsing cats
		// triggering a reset now looses any client side data we may have had
		this.resetCategories = true;
		return this.parseCats(data);
	}

	/** @override */
	constructor(models, opts = {}) {
		// https://stackoverflow.com/questions/37127083/can-two-files-have-each-other-as-dependencies-in-javascript-when-using-webpack
		//Model = require('../models/category');
		opts.model = opts.model || Model;

		super(models, opts);

		// Set categoryid if provided
		this.categoryid = opts.categoryid;

		/**
		 * @type {array}
		 * @default
		 */
		this.disabledCategories = opts.disabledCategories || [];

		/**
		 * Will hold our desired category order for `setCategoryOrder`
		 * @type {array}
		 * @default
		 */
		this.order = opts.order || [];

		/**
		 * @type {number}
		 * @default
		 * 0 = manual (provided order)
		 * 1 = alphabetical asc (normal)
		 * 2 = alphabetical desc (reverse)
		 */
		this.orderType = opts.orderType || 0;

		/**
		 * @type {string}
		 * @default
		 */
		this.searchString = opts.searchString || '';

		/**
		 * Target category id
		 * @type {number}
		 * @default
		 */
		this.targetCategory = opts.targetCategory;

		// We reference this a lot
		var topLevelId = customConfig.categoryid;

		// Debounced methods
		this.updateTopLevelCategories = _.debounce(
			this.updateTopLevelCategories,
			100,
		);

		// We are top level
		// We provide a top level collection and a filtered collection
		if (this.categoryid === topLevelId) {
			// Do not pass a categoryid to these collections - always know they are "top level" (flat)
			// if you pass a categoryid you will wind up in a maximum execution error
			this.topLevelCategories = new this.constructor([]);
			this.filteredCategories = new this.constructor([]);
		}

		// Children of a category have changed
		const debouncedFetchTopLevel = _.debounce((id) => {
			this.fetchChildren(this.categoryid, {duplicateQueryPolicy: 'abort'}).then(
				this.updateTopLevelCategories,
			);
		}, 100);

		// Hey, listen!
		this
			// On reset/sync we need to put back /update our top levels
			.listenTo(
				this,
				'reset sync',
				_.debounce((collection) => {
					// Make sure this concerns us (sync could come from a model within)
					if (
						this.topLevelCategories &&
						this._listenId === collection._listenId
					) {
						this.updateTopLevelCategories();
					}
				}, 100),
			)
			// WARNING:
			// Since these are dispatch listeners and not targeted solely at us - we can not debounce the listeners
			.listenTo(dispatcher, 'category:children:changed', (id) => {
				// Main tree
				// All other models/collections in relation to this tree should have references
				// to us and our models - if not then there is some flawed logic somehwere that
				// will need addressing.
				// Top level trees are the only responders
				if (this.categoryid === topLevelId) {
					id = parseInt(id);
					// Was a top level that changed
					if (id === this.categoryid) {
						// It gets too messy to not update the whole top level in this case
						// The category model itself should be responding to data changes
						debouncedFetchTopLevel(id);
					}
				}
			})
			// Category deleted
			.listenTo(dispatcher, 'category:delete:success', (model) => {
				// Regardless of where it was deleted from - it is gone - get it out of all collections
				this.remove(model.id);
			})
			// Categories changed
			.listenTo(
				dispatcher,
				'category:edit:success',
				(model, isNew, changedParent) => {
					// Only top level responds to this
					if (model instanceof Model && this.categoryid === topLevelId) {
						model.set({
							// TODO: These are strings on a save
							// Need to handle in set and make sure they are enforced as ints
							categoryid: parseInt(model.get('categoryid')),
							parentid: parseInt(model.get('parentid')),
						});

						// New - add to our collection
						if (isNew) {
							this.selectedCategory = model.id;
							// Clone it so that different trees do not start tracking open and closestuff
							this.add(model.clone());
						}

						// Top level edit
						// TODO: should we also be checking that the only ancestor matches Netx.category.options.categoryid?
						// If you have only one ancestor and its not the top level id - something is terribly wrong(?)
						var ancestors = model.get('ancestors') || [];
						if (ancestors.length === 1 && ancestors[0] === topLevelId) {
							this.updateTopLevelCategories();
						}
					}
				},
			)
			// Simulate progress / syncing when moving a category (and its children)
			.listenTo(dispatcher, 'category:move:confirmed', () => {
				// Top level trees are the only responders
				if (this.categoryid === topLevelId) {
					// Mimic read
					this.trigger('request:read', this, undefined, {});
					this.listenToOnce(dispatcher, 'category:move:success', (cat) => {
						this.stopListening(dispatcher, 'category:move:error');
						// Mimic sync
						this.trigger('sync', this, cat, {});
					});
					this.listenToOnce(dispatcher, 'category:move:error', (error) => {
						this.stopListening(dispatcher, 'category:move:success');
						// This will trigger a fetch on us and get the sync event when done
						dispatcher.trigger('category:tree:refresh', this);
					});
				}
			})
			// Close all categories in tree
			// category:tree:reset means 'close all nodes'
			// category:tree:close:all means the same thing
			.listenTo(
				dispatcher,
				'category:tree:reset category:tree:close:all',
				(tree) => {
					// Make sure we are the tree this was requested on
					if (tree._listenId === this._listenId) {
						this.closeAll();
					}
				},
			);
	}

	/**
	 * @method
	 */
	clearSearch() {
		this.searchString = '';
	}

	/**
	 * @method
	 * @param {Netx.Collections.CategoryTreeNodes} tree Category tree to clone from
	 */
	cloneFrom(tree) {
		this.reset(
			tree.map((cat) => {
				return cat.clone(this);
			}),
		);
		this.disabledCategories = [];
		this.selectedCategory = null;
	}

	/** @method */
	closeAll() {
		// Calls the method on each model in this collection
		this.invoke('close', {recursive: true});
	}

	/**
	 * @method
	 * @param {number} catid Category id
	 * @param {object} opts Request options
	 */
	// TODO: unwrap!
	fetchCategory(catid, opts = {}) {
		// See if we already have an instance of this category model
		// If so, run the fetch through it
		const cat = this.get(catid);
		if (cat) {
			return cat.fetch(_.extend({duplicateQueryPolicy: 'merge'}, opts));
		}

		// Set our target category id
		this.targetCategory = catid;

		// Mimic read
		!opts.silent && this.trigger('request:read', this, null, opts);

		// Promise
		return this._fetchCategory(
			_.extend({duplicateQueryPolicy: 'merge'}, opts),
		).then((response) => {
			// Unset target category
			this.targetCategory = null;
			// Open if it was previously open
			const cat = this.get(catid);
			// If cat was open, re-open
			cat && cat.get('children').length > 0 && cat.get('open') && cat.open();
			// Trigger sync if not silent
			!opts.silent && this.trigger('sync', this, response, opts);

			return cat;
		});
	}

	/**
	 * fetchChildren fetches all the child categories of the specified category.
	 * @method
	 * @param {number} catid Category id
	 * @param {object} opts Request options
	 * @fires module:category#category:fetchedChildren
	 */
	// TODO: unwrap!
	fetchChildren(catid, opts = {}) {
		this.targetCategory = catid;

		var cat = this.get(catid);
		dispatcher.trigger('category:' + catid + ':fetchingChildren', this, opts);

		// Mimic read
		!opts.silent && cat && cat.trigger('request:read', cat, null, opts);

		// Promise
		const p = this._fetchChildren(opts);

		p.then((response) => {
			dispatcher.trigger('category:' + catid + ':fetchedChildren');
			dispatcher.trigger('category:fetchedChildren', catid);

			// If cat was set to be open - open it (does not matter if it is already opened or not)
			cat = this.get(catid);

			!opts.silent && cat && cat.trigger('sync', cat, null, opts);
		});

		return p;
	}

	/**
	 * Find and return the selected category model
	 * @method
	 * @returns {object} category model
	 */
	getSelectedCategoryModel() {
		let category;
		// DAM-15378 - if we are filtering, look in filteredCategories
		if (this.filteredCategories.length) {
			category = this.filteredCategories.get(this.selectedCategory);
		}
		return category || this.get(this.selectedCategory);
	}

	/**
	 * Take an array of objects, convert each into a Category model, and add them to our collection.
	 * @method
	 * @param {array} data Array of category data
	 */
	parseCats(data) {
		// If empty - do a reset - otherwise we end up running through the add method a ton in childViews
		// and can not utilize the breakItUp method
		if (!this.length || this.resetCategories) {
			this.reset(
				_.map(data, (c) => {
					// Make sure we maintain our childCategories and open references (both are client attributes and should not be affected by the return from the server)
					return new this.model(c, {collection: this});
				}),
			);
		}
		// Just add
		else {
			this.add(
				_.compact(
					_.map(data, (c) => {
						// Merge directly into any existing so that we may maintian the true reference and the attributes se ton the client side
						var existing = this.get(c.categoryid);
						if (existing) {
							existing.set(c);
						} else {
							// Make sure we maintain our childCategories and open references (both are client attributes and should not be affected by the return from the server)
							return new this.model(
								_.extend({}, existing ? existing.attributes : {}, c),
								{collection: this},
							);
						}
					}),
				),
				{
					silent: true,
				},
			);
		}

		// Set to false as we are done - regardless
		this.resetCategories = false;

		return this.models;
	}

	/**
	 * Take an array of objects, check if the category is already in Netx.category.tree, fetch any that are not,
	 * convert each into a Category model, and add them to our collection.
	 * @method
	 * @param {array} data Array of category data
	 * @param {object} opts Request options
	 */
	parseSearchCats(data, opts) {
		const promises = [];
		const tree = Netx.category.tree;
		let toAdd = [];

		// Loop over data and look for categories we will need to fetch from the server
		this.neededCats = _.compact(
			_.map(data, (c) => {
				const catId = parseInt(c.value);
				const category = tree.get(catId);
				// Do not want the "0" category (more option)
				if (!isNaN(catId) && catId > 0) {
					if (!category || !category.attributes.length) {
						return c.value;
					}
					toAdd.push(category);
				}
			}),
		);

		// Need to fetch some needed categories
		if (this.neededCats.length) {
			promises.push(
				this._getCategoryObjects({...opts, duplicateQueryPolicy: 'abort'}).then(
					(response) => {
						// Turn response into an array of category models and concat them onto the categories we already had
						toAdd = toAdd.concat(
							response.map((c) => new Model({...c, collection: this})),
						);

						// We are already updating the `main` tree - don't do it twice
						if (this._listenId !== tree._listenId) {
							this.reset(toAdd);
						}
						// Update `main` tree
						// tree.add( toAdd, { silent: true } );
						this.neededCats = null;
						return response;
					},
				),
			);
		}
		// We have everything we need
		else {
			this.reset(toAdd);
		}

		return $.when.apply($, promises);
	}

	/**
	 * @method
	 * @param {string} str  Category name to search
	 * @param {object} opts Request options
	 */
	// TODO: unwrap!
	searchCategoryName(str, opts = {}) {
		this.searchString = str;

		// Run through our filtered collection - that is what it is for
		if (this.filteredCategories) {
			return this.filteredCategories.searchCategoryName(str, opts);
		}

		this.reset();

		if (!opts.silent) {
			this.trigger('request:read', this, null, opts);
		}
		return this._searchCategoryName(opts).then((response) => {
			return this.parseSearchCats(response, opts).then((_response) => {
				!opts.silent && this.trigger('sync', this, _response, opts);
				return _response;
			});
		});
	}

	/**
	 * @method
	 * @param {array} cats Array of categories in the order desired
	 * @param {object} opts Request options
	 */
	// TODO: unwrap!
	setCategoryOrder(cats, opts) {
		this.order = _.map(cats, (catid, idx) => {
			return {categoryid: catid, order: idx};
		});
		return this._setCategoryOrder(opts).then(() => {
			// Update category models
			_.each(this.order, (orderObj) => {
				const cat = this.get(orderObj.categoryid);
				cat && cat.set('orderby', orderObj.order);
			});
		});
	}

	/**
	 * Order in which to display the returned categories
	 * 0 = manual (provided order);
	 * 1 = alphabetical asc (normal);
	 * 2 = alphabetical desc (reverse);
	 * @method
	 * @param {integer} type 0,1,2
	 */
	setCategoryOrderType(type) {
		this.orderType = type;
		return this.orderType;
	}

	/**
	 * @method
	 */
	updateTopLevelCategories() {
		const topLevelCategories = this.topLevelCategories;
		topLevelCategories.reset(this.where({parentid: customConfig.categoryid}));
		topLevelCategories.each((model) => {
			const category = this.get(model.id);
			if (!category) return;
			// If we were open - re-open
			// Run through the controller instead of the model
			category.get('open') &&
				dispatcher.trigger('category:tree:open', model, this);
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'CategoryTreeNodes';
	}
}
